import React, { useState } from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath } from 'react-katex';

// Simple approach: split by $$ and render odd indices as math
function SimpleDebugMath({ text }) {
  if (!text) return null;
  
  return (
    <div>
      {text.split('$$').map((chunk, i) =>
        i % 2 === 1
          ? <InlineMath key={i} math={chunk} />
          : <span key={i}>{chunk}</span>
      )}
    </div>
  );
}

// Improved approach with regex that handles multi-line LaTeX
function RegexDebugMath({ text }) {
  if (!text) return null;
  
  // Use regex that supports multi-line LaTeX content with /s flag (dotAll)
  const segments = [];
  const pattern = /\$\$(.+?)\$\$/gs;
  let lastIndex = 0;
  let match;
  
  while ((match = pattern.exec(text)) !== null) {
    // Add any text before this math block
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: text.slice(lastIndex, match.index)
      });
    }
    
    // Add the math content (captured inside $$ delimiters)
    segments.push({
      type: 'math',
      content: match[1]
    });
    
    // Update last position
    lastIndex = pattern.lastIndex;
  }
  
  // Add any remaining text after the last math block
  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.slice(lastIndex)
    });
  }
  
  // Render each segment appropriately
  return (
    <div>
      {segments.map((segment, index) => {
        if (segment.type === 'math') {
          // Render math content
          try {
            return <InlineMath key={index} math={segment.content} />;
          } catch (e) {
            console.error("KaTeX error:", e);
            return <span key={index} className="math-error">{segment.content}</span>;
          }
        } else {
          // Render regular text
          return <span key={index}>{segment.content}</span>;
        }
      })}
    </div>
  );
}

// Special handling for Line: tags
function LineTagDebugMath({ text }) {
  if (!text) return null;
  
  // Special handling for Line: tags
  const isLineTag = text.trim().startsWith('Line:');
  if (isLineTag) {
    // Extract the part after "Line: " to process it
    const linePrefix = 'Line: ';
    const lineContent = text.substring(text.indexOf(linePrefix) + linePrefix.length);
    
    // Create a Line: prefix component
    const lineTag = <strong key="line-tag">Line:</strong>;
    
    // Process the content separately with the regex approach
    return (
      <div>
        {lineTag}{' '}
        <RegexDebugMath text={lineContent} />
      </div>
    );
  }
  
  // For regular content (not Line: tags), use the regex approach
  return <RegexDebugMath text={text} />;
}

// A simple test harness that lets you paste in your own LaTeX content
export default function TestMathRendering() {
  const [testText, setTestText] = useState(`Now we need to find all values of $$ X $$ in the range $$ 0° <= X <= 360° $$.\n\nThe value of angle whose cosine is $$ \\sqrt{3}/2 $$ is $$ \\pi/6 $$ radians or 30°.`);
  
  // Pre-defined examples
  const examples = [
    {
      title: "Simple example",
      text: `Now we need to find all values of $$ X $$ in the range $$ 0° <= X <= 360° $$.`
    },
    {
      title: "Multi-line LaTeX example",
      text: `This is an example of multi-line LaTeX:
$$ x^2 + y^2 = r^2
\\quad \\text{(Pythagorean circle equation)} $$
which should be handled correctly.`
    },
    {
      title: "Mismatched delimiters example",
      text: `In summary, you correctly identified the statement as false and $$ r = -1/2 . This represents the probability that neither event occurs.`
    },
    {
      title: "Line tag example",
      text: `Line: $$ x^2 + y^2 = r^2 $$`
    },
    {
      title: "Line tag with error example",
      text: `Line: $$ cos(2x) = sqrt(3)/2 $$
[ERROR] This line incorrectly uses cos^-1(sqrt(3)/2). Remember that $$ \\cos(\\pi/2) = 0 $$, not $$ \\sqrt{3}/2) $$.`
    },
    {
      title: "Example from user query (italicized text issue)",
      text: `but $$ cos⁻¹(\\sqrt{3}/2) $$ is not $$ \\pi/2 $$.  Remember that $$ \\cos(\\pi/2) = 0 $$, not $$ \\sqrt{3}/2 $$.  The value of angle whose cosine is $$ \\sqrt{3}/2 $$ is $$ \\pi/6 $$ radians or 30°. Also, the general solution for $$ \\cos(y) = c $$ involves $$ \\pm \\cos^{-1}(c) $$.`
    }
  ];
  
  return (
    <div style={{ fontFamily: 'Arial, sans-serif', maxWidth: '1000px', margin: '0 auto', padding: '20px' }}>
      <h1>Math Rendering Test</h1>
      
      <div style={{ marginBottom: '30px' }}>
        <h2>Try Your Own LaTeX Content</h2>
        <p>Paste LaTeX content below to see how different rendering approaches handle it:</p>
        <textarea 
          value={testText}
          onChange={(e) => setTestText(e.target.value)}
          style={{ 
            width: '100%', 
            height: '120px', 
            padding: '10px',
            fontFamily: 'monospace'
          }}
        />
        
        <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
          <div style={{ flex: '1 1 300px', border: '1px solid #ddd', padding: '15px', borderRadius: '5px' }}>
            <h3>Simple Split Approach (Current Implementation)</h3>
            <div style={{ backgroundColor: '#f9f9f9', padding: '10px', minHeight: '100px' }}>
              <SimpleDebugMath text={testText} />
            </div>
            <p style={{ fontSize: '14px', color: '#666' }}>
              <em>This approach splits on $$ and assumes odd indices are math expressions. 
              It breaks on multi-line LaTeX and causes issues when LaTeX is unclosed.</em>
            </p>
          </div>
          
          <div style={{ flex: '1 1 300px', border: '1px solid #ddd', padding: '15px', borderRadius: '5px' }}>
            <h3>Regex Approach (Improved)</h3>
            <div style={{ backgroundColor: '#f9f9f9', padding: '10px', minHeight: '100px' }}>
              <RegexDebugMath text={testText} />
            </div>
            <p style={{ fontSize: '14px', color: '#666' }}>
              <em>This approach uses regex with the dotAll flag to properly match $$ pairs across lines.
              It correctly handles multi-line LaTeX and prevents unwanted italicization.</em>
            </p>
          </div>
          
          <div style={{ flex: '1 1 300px', border: '1px solid #ddd', padding: '15px', borderRadius: '5px' }}>
            <h3>Line Tag-Aware Approach (Best)</h3>
            <div style={{ backgroundColor: '#f9f9f9', padding: '10px', minHeight: '100px' }}>
              <LineTagDebugMath text={testText} />
            </div>
            <p style={{ fontSize: '14px', color: '#666' }}>
              <em>This approach adds special handling for "Line:" tags to preserve them correctly,
              while also using the improved regex method for LaTeX processing.</em>
            </p>
          </div>
        </div>
      </div>
      
      <h2>Pre-defined Test Cases</h2>
      {examples.map((example, index) => (
        <div key={index} style={{ 
          marginBottom: '30px', 
          border: '1px solid #ddd', 
          padding: '15px', 
          borderRadius: '5px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <h2>{example.title}</h2>
          
          <div style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '10px', 
            borderRadius: '5px', 
            marginBottom: '15px',
            border: '1px solid #e0e0e0' 
          }}>
            <h3>Raw Text:</h3>
            <pre style={{ whiteSpace: 'pre-wrap', margin: '0' }}>{example.text}</pre>
          </div>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
            <div style={{ flex: '1 1 300px' }}>
              <h3>Current Implementation:</h3>
              <div style={{ 
                border: '1px solid #ccc', 
                padding: '10px', 
                borderRadius: '5px',
                backgroundColor: '#fff',
                minHeight: '100px' 
              }}>
                <SimpleDebugMath text={example.text} />
              </div>
            </div>
            
            <div style={{ flex: '1 1 300px' }}>
              <h3>Improved Implementation:</h3>
              <div style={{ 
                border: '1px solid #ccc', 
                padding: '10px', 
                borderRadius: '5px',
                backgroundColor: '#fff',
                minHeight: '100px' 
              }}>
                <LineTagDebugMath text={example.text} />
              </div>
            </div>
          </div>
        </div>
      ))}
      
      <div style={{ marginTop: '40px', borderTop: '1px solid #ddd', paddingTop: '20px' }}>
        <h2>Implementation Details</h2>
        <p>Here's how to update the <code>processMathDelimiters</code> function in <code>MathWorkflow.js</code>:</p>
        
        <div style={{ 
          backgroundColor: '#f8f8f8', 
          padding: '15px', 
          borderRadius: '5px',
          border: '1px solid #ddd',
          fontFamily: 'monospace',
          fontSize: '14px',
          whiteSpace: 'pre-wrap',
          overflow: 'auto',
          maxHeight: '500px'
        }}>
{`const processMathDelimiters = (content) => {
  // If no math delimiters, return the original text
  if (!content.includes('$$')) return content;
  
  // Special handling for Line: tags
  const isLineTag = content.trim().startsWith('Line:');
  if (isLineTag) {
    // Extract the part after "Line: " to process it
    const linePrefix = 'Line: ';
    const lineContent = content.substring(content.indexOf(linePrefix) + linePrefix.length);
    
    // Create a Line: prefix component
    const lineTag = <strong key="line-tag">Line:</strong>;
    
    // Process the content separately using the regex approach below
    const processedContent = processMathContent(lineContent);
    
    // Return a fragment with the Line: prefix and processed content
    return (
      <>
        {lineTag}{' '}
        {processedContent}
      </>
    );
  }
  
  // For regular content (not Line: tags), use the regex approach
  return processMathContent(content);
};

// Helper function to process math content with regex
const processMathContent = (content) => {
  if (!content.includes('$$')) return content;
  
  // Use regex that supports multi-line LaTeX content with /s flag (dotAll)
  const segments = [];
  const pattern = /\$\$(.+?)\$\$/gs;
  let lastIndex = 0;
  let match;
  
  while ((match = pattern.exec(content)) !== null) {
    // Add any text before this math block
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: content.slice(lastIndex, match.index)
      });
    }
    
    // Add the math content (captured inside $$ delimiters)
    segments.push({
      type: 'math',
      content: match[1]
    });
    
    // Update last position
    lastIndex = pattern.lastIndex;
  }
  
  // Add any remaining text after the last math block
  if (lastIndex < content.length) {
    segments.push({
      type: 'text',
      content: content.slice(lastIndex)
    });
  }
  
  // Render each segment appropriately
  return segments.map((segment, index) => {
    if (segment.type === 'math') {
      // Render math content
      try {
        return <InlineMath key={index} math={segment.content} />;
      } catch (e) {
        console.error("KaTeX error:", e);
        return <span key={index} className="math-error">{segment.content}</span>;
      }
    } else {
      // Render regular text
      return <span key={index}>{segment.content}</span>;
    }
  });
};`}
        </div>
      </div>
    </div>
  );
} 